// ============================================================================
// lib/modules/registry — каталог модулей и блоков.
// ============================================================================
// Один источник правды о том, какие модули существуют, к чему относятся и что
// они приносят. Раньше это знание было размазано: линия бизнеса в
// company-modules, признаки в features, глобальные флаги в app_flags.
//
// НЕ 'use server' — синхронные данные и чистые функции.
//
// ── Два вида модулей, и путать их нельзя ──────────────────────────────────
//
//   'core'  — ЯДРО ЛИНИИ. Задаётся тем, кто клиент: экспедитор возит,
//             импортёр торгуется. Клиент это не переключает, и на этом
//             держится разница тарифов: дай экспедитору тендеры — и импортёр
//             зарегистрируется экспедитором ради цены впятеро ниже.
//
//   'addon' — ДОПОЛНЕНИЕ. Ставится поверх любой линии, продаётся отдельной
//             строкой, включается тарифом или админкой.
//
// ── Про события модуля ────────────────────────────────────────────────────
// Каждый модуль объявляет ключи событий (`eventKeys`) — что в нём можно
// заметить. Из этих объявлений движок правил собирает список доступных
// событий: включили модуль — правил стало больше, выключили — его правила
// ушли вместе с ним.
//
// 950: рядом лежал ещё список тех же событий ТЕКСТОМ, по-русски, «для
// покупателя». Его никто не читал: тарифы и онбординг берут описание модуля
// из словаря. Список убран, осталось одно объявление — ключами.
// ============================================================================

export type ModuleKind = 'core' | 'addon'

/** Ключ модуля. Совпадает с ключом в companies.modules и app_flags. */
export type ModuleKey = 'transport' | 'tenders' | 'management' | 'stock' | 'reputation'

export interface ModuleDef {
  key: ModuleKey
  kind: ModuleKind
  /**
   * Названия и описания здесь НЕТ намеренно.
   *
   * До 1 сентября они лежали полями `title`/`summary` рядом с ключом — и
   * одновременно в `messages/<локаль>/modules.json` для клиента. Два текста об одном
   * товаре: правка в одном месте молча расходилась с другим, а у трёх модулей
   * из пяти вторая половина вовсе отсутствовала — клиент видел на регистрации
   * `modules.transport.title`.
   *
   * Источник один: файлы переводов. Читать — через `moduleTitle` и
   * `moduleSummary` из `lib/modules/i18n`. Наличие ключа стережёт
   * `scripts/check-i18n-registry.py`.
   */
  /**
   * Какие события движка правил даёт модуль — КЛЮЧАМИ, а не текстом.
   *
   * Раньше здесь лежал человеческий список, и он разошёлся с делом: реестр
   * обещал «Договор или документ истекает», а такого события в движке нет
   * вовсе; три работающих, наоборот, не были обещаны. Покупатель видел одно,
   * получал другое.
   *
   * Импортировать сюда сам список EVENTS нельзя: тот помечен `server-only`, а
   * реестр тянут три клиентских компонента. Поэтому связь держит ТЕСТ
   * (`lib/modules/registry.test.ts`): ключ, которого нет в движке, роняет его.
   *
   * Тексты для человека — в `messages/<локаль>/modules.json` по тем же ключам.
   */
  eventKeys?: readonly string[]
  /**
   * Маршруты, которые принадлежат модулю, — префиксами внутри `(app)`.
   *
   * Объявляются ЗДЕСЬ, а не перечисляются в сайдбаре и на каждой странице.
   * Раньше каждый пункт меню держал свою проверку модуля руками: добавить
   * модуль значило вспомнить про сайдбар, про тарифы и про каждую страницу —
   * и склад однажды забыли в тарифах именно так.
   *
   * По этому списку `scripts/check-module-routes.py` проверяет, что у КАЖДОЙ
   * страницы модуля стоят оба гейта: готовность блока и выдача компании.
   * Забыть гейт становится невозможно, а не «нежелательно».
   */
  routes?: readonly string[]
  /**
   * Цена дополнения. У ядер её нет: они входят в тариф.
   *
   * Живёт ЗДЕСЬ, а не на экранах: иначе тарифы, регистрация и коммерческое
   * предложение начнут расходиться, и однажды клиенту назовут одну цену, а
   * счёт придёт на другую.
   *
   * Год = десять месяцев, как у тарифов: два месяца в подарок. Правило одно на
   * всю систему, иначе счёт становится непонятным.
   */
  price?: {
    monthly: number
    yearly: number
    currency: string
    /**
     * За что берём деньги.
     *
     * `period` — подписка: 29 € в месяц, 290 в год. Идёт сама, пока не
     * отключат.
     * `unit` — расходуемая единица: 1,2 € за проверку. Кончается и покупается
     * снова.
     *
     * Показывать их одинаково нельзя. «1,2 € в месяц» у проверок — прямая
     * неправда, а именно так их и напишет экран, если вида цены нет: у
     * `monthly` другого смысла в разметке не предусмотрено.
     */
    kind?: 'period' | 'unit'
    /**
     * Что покупают штуками — кодом (916-д8): слово на языке человека собирают
     * экраны («за проверку», «per check»). Пусто у подписок.
     */
    unit?: 'check'
    /**
     * Цена разовой покупки, когда она дороже штуки в пакете.
     *
     * Нужна, чтобы на витрине стояло «от 1,2 € · разовая 3 €»: одна цифра без
     * второй не объясняет, почему пакет выгоден.
     */
    singlePrice?: number
  }
  /**
   * Модуль ПРОДАЁТСЯ как отдельный продукт: у него есть цена, карточка в
   * тарифах и строка при регистрации.
   *
   * Не у всех дополнений так: часть из них входит в другой продукт и своей
   * цены не имеет.
   */
  sold?: boolean
  /**
   * Входит в состав другого продукта.
   *
   * Покупатель платит один раз за целое, но может выключить часть, которая ему
   * не нужна: экспедитору без товара торговый контур ни к чему, и держать его
   * в меню — только мешать. Цена при этом не меняется: продукт один.
   *
   * Разделение остаётся техническим — так и было задумано: задачи работают у
   * любой компании, торговый контур только у торгующих.
   */
  partOf?: ModuleKey
  /**
   * Глобальный флаг готовности. true — модуль ещё строится, и до включения
   * флага в админке его не видит никто, даже оплативший клиент.
   */
  gated?: boolean
  /** Что в этом модуле умеет заметить система. Питает движок правил. */
  /**
   * Человеческий список для ЯДЕР — они продаются тарифом, и их описание живёт
   * здесь. У дополнений вместо него `eventKeys`: там обещание обязано сходиться
   * с движком, и держит это тест.
   */
}

// `as const satisfies` вместо аннотации типом: аннотация расширяет литералы до
// string и boolean, и вывести из реестра что-либо становится нельзя. Здесь же
// проверка соответствия ModuleDef остаётся, а литералы сохраняются — на них
// строится AppFlag в lib/flags/app-flags.
export const MODULES = [
  {
    key: 'transport',
    kind: 'core',
    // Назывался «Грузы и сделки», и это вводило в заблуждение: ГРУЗЫ — это
    // отдельная вещь, лента из мессенджеров и бирж (фича `cargo_feed`,
    // таблицы cargo_messages/cargo_items, экран /cargo). В этом модуле её нет.
    // Здесь сделки, партнёры, автопарк, документы и границы.
    // Обещание движку: эти шесть правил существуют в реестре событий
    // (1002-Б · Б2: «страховка груза» снята вместе со своим полем, 800).
    // Текст выше читает покупатель, ключи ниже проверяет тест — оба конца
    // стерегут друг друга.
    eventKeys: [
      // Ревизия правил 07.10.2026: сроки документов (и «не внесены») — одно
      // правило на машину и одно на водителя.
      'vehicle_docs_expiring',
      'driver_docs_expiring',
    ],
    // ── МАРШРУТЫ ЛИНИИ ─────────────────────────────────────────────────────
    // Объявлены 18 августа. До этого у ядра маршрутов не было, и
    // принадлежность разделов линии жила ручными проверками `&& mod.transport`
    // в сайдбаре — то есть в меню, а меню защитой не является.
    //
    // Теперь `moduleOfRoute` отвечает на вопрос «чей это раздел» из одного
    // места: и для фильтра меню, и для гейта в layout раздела.
    // 946-B (решение владельца): лиды — ядро, общая коммерческая сущность:
    // будущий заказчик у экспедитора, будущий покупатель у импортёра.
    // FpBUMI6e: «Документы» — общие для перевозки и торговли (счёт заказа
    // пишется туда же), поэтому раздел не принадлежит одной линии: его
    // закрывает requireAnyModule(['stock', 'transport']) в layout раздела.
    routes: ['deals', 'fleet'],
  },
  {
    key: 'tenders',
    kind: 'core',
    // ── ОБЕЩАНИЕ МАШИНЕ ─────────────────────────────────────────────────
    // `eventKeys` — то, чем проверяется, что обещанное покупателю правило
    // действительно существует в движке.
    //
    // До этого у ядер был только текст, и обещание «Торги закрылись, победитель
    // не выбран» висело в тарифах, пока самого правила не было. Тест
    // `каждое событие движка кем-то обещано` ловит обратный случай: правило
    // есть, а обещания нет. Оба нужны — они стерегут разные концы.
    // Заход 764 развёл одно событие на два взаимоисключающих: открытые торги
    // с прошедшим сроком и закрытые без победителя.
    //
    // Ревизия правил 07.10.2026: «Тендер без победителя» (tender_no_winner)
    // слит сюда — вариант «закрыты без победителя» того же правила.
    eventKeys: [
      'tender_bidding_expired',
      // 01.10.2026: черновик не опубликован, ставок нет перед сроком.
      'tender_draft_not_published',
      'tender_no_bids',
    ],
    // Ядро линии импортёра. На этом маршруте держится разница тарифов —
    // подробности в layout раздела.
    routes: ['tenders'],
  },
  {
    key: 'management',
    kind: 'addon',
    // 29 € — 59% от Pro и 33% от Business.
    //
    // Изначально стояло 19 €: тогда продукт был только задачами и правилами.
    // Сейчас в него входит весь торговый контур — склад, закупки, заказы, цены,
    // деньги, договоры, доставка, план продаж. Это по объёму сопоставимо со
    // второй системой, и треть от тарифа за такое — недооценка: дешёвая цена
    // говорит покупателю, что внутри мало.
    //
    // Верхняя граница здесь около 39 €: дальше добавка начинает читаться как
    // альтернатива тарифу, и человек считает, что дешевле взять другой план.
    price: { monthly: 29, yearly: 290, currency: 'EUR', kind: 'period' },
    sold: true,
    gated: true,
    // ── РАЗДЕЛЫ ДОПОЛНЕНИЯ, НЕ ЗАВИСЯЩИЕ ОТ ТОРГОВЛИ ──────────────────────
    // Решение владельца 18 августа: расходы — часть «Менеджмента», который
    // докупает ЛЮБАЯ линия. (Здесь же был «План продаж» — удалён как дубль:
    // план сотрудника живёт в мотивации, см. lib/compensation/employee-plan.)
    //
    // Ключевое здесь — модуль `management`, а НЕ его торговая часть `stock`.
    // Владелец может выключить торговлю (экспедитору без товара склад только
    // мешает в меню), и раздел обязан при этом остаться:
    //
    //   РАСХОДЫ — у перевозчика расходы рейса И ЕСТЬ его заработок
    //   (выручка минус они). Товара у него нет, а расходы есть.
    //
    // До 18 августа оба числились за `transport`, а страницы требовали
    // `stock`. Перевозчик видел пункт в меню и получал редирект на дашборд:
    // реестр отдал раздел одному модулю, страница спрашивала про другой.
    routes: ['tasks', 'expenses'],
    eventKeys: [
      // План продаж: событие про работу менеджера, а не про документ.
      'sales_plan_behind',
      // 01.10.2026: заявка на отсутствие ждёт решения, уровень зарплаты не назначен.
      'absence_request_pending',
      'employee_no_salary_grade',
      // 824: менеджмент следил за одним событием на весь модуль.
      'partner_no_manager',
      'task_unassigned_overdue',
      'task_postponed_often',
      'contact_no_outcome',
      // Ревизия правил 07.10.2026: включает «нечем связаться» (прежде lead_no_contact).
      'lead_untouched',
      'debt_overdue',
      // Заход 763: кредиторка. Прежде отслеживалась только дебиторка —
      // `payable` во всём файле событий не встречался ни разу.
      'debt_payable_overdue',
      // 01.10.2026: долг без срока, неразнесённая выписка.
      'debt_no_due_date',
      'bank_rows_unmatched',
      'deal_stale',
      'shipment_unloaded_not_completed',
      'client_went_quiet',

      // ── ПРАВИЛА ГИГИЕНЫ ──────────────────────────────────────────────────
      // Условия перенесены из проверок раздела «Здоровье», где считали цифру
      // для владельца. Здесь та же проверка становится задачей тому, кто может
      // починить.
      //
      // Обещание обязательно: тест `lib/modules/registry.test.ts` требует,
      // чтобы КАЖДОЕ событие движка кто-то обещал. Иначе правило существует, но
      // не принадлежит ни одному модулю — и при выдаче состава клиенту его
      // судьба не определена.
      'deal_no_manager',
      'partner_contact_no_reach',
      'deal_negative_margin',
      'blacklisted_partner_active',

      // «Что дальше» — обычный ход сделки, лида, партнёра (30.09.2026): шаг
      // записи — только задача от правила, а правила рождает этот движок.
      'deal_no_transport',
      'deal_no_driver',
      'deal_no_carrier_price',
      'deal_vehicle_docs_expired',
      'deal_loading_overdue',
      'lead_qualified_not_converted',
      // Ревизия правил 07.10.2026: реквизиты и контакт — одно правило.
      'partner_incomplete',
      // 1003: «Получить оплату» — обычный ход после завершения сделки.
      'deal_payment_due',
      // Ход сделки до конца (01.10.2026): ставка клиента, дата и адреса
      // погрузки, условия оплаты; просроченная доставка, низкая маржа.
      // Ревизия правил 07.10.2026: четыре «Нет …» сделки — одно правило.
      'deal_incomplete',
    ],
  },
  {
    key: 'reputation',
    kind: 'addon',
    // ── ЦЕНА ЗА ШТУКУ, А НЕ ЗА МЕСЯЦ ────────────────────────────────────
    // Проверка — расходуемая единица: одна проверка, одно досье, оно остаётся
    // у компании навсегда. Подписки здесь нет и быть не может: человек может
    // не проверять никого месяцами, и брать с него за это деньги не за что.
    //
    // `monthly` и `yearly` заполнены ценой ОДНОЙ проверки: поля обязательны по
    // виду записи, а вид цены `unit` говорит экрану, как их читать. Оставить их
    // нулями было бы хуже — экран показал бы «0 €».
    price: {
      // `monthly` — цена в САМОМ БОЛЬШОМ пакете: с неё начинается «от».
      // `yearly` не используется у штучных цен, но поле обязательно по виду
      // записи — держим равным, чтобы никто не вывел из него скидку за год.
      monthly: 1.2, yearly: 1.2, currency: 'EUR',
      kind: 'unit', unit: 'check',
      // Цена одной проверки, купленной отдельно. Она заметно выше штуки в
      // пакете — именно эта разница и заставляет брать пакет.
      singlePrice: 3,
    },
    sold: true,
    // Раздел «Репутация» виден ВСЕГДА, даже тому, кто ничего не покупал:
    // вопросы о его компании задают в каталоге независимо от покупок, и
    // спрятать их значит лишить человека возможности ответить.
    //
    // Поэтому маршрута здесь нет: страница не закрывается выдачей модуля.
    // Платное внутри — сами проверки, и они ограничены остатком, а не доступом.
  },
  {
    key: 'stock',
    kind: 'addon',
    // Часть «Менеджмента», а не отдельный товар: владелец платит один раз, но
    // может выключить торговый контур, если возит грузы и не торгует товаром.
    partOf: 'management',
    gated: true,
    // `expenses` переехал в `management` — см. пояснение там.
    // Каждый раздел модуля — здесь: незнакомый маршрут реестр считает
    // разрешённым, и «Запросы поставщикам», пока были разделом без этой
    // записи, висели в меню и с выключенной торговлей. Теперь это сегмент
    // /offers?side=buy; старый адрес переадресует посредник (moved-pages.ts).
    routes: ['stock', 'pricing', 'purchases', 'orders', 'offers', 'seasonal'],
    eventKeys: [
            // ── ЗАКУПКИ И ЗАПРОСЫ ────────────────────────────────────────────────
      // Тот же круг работы, что склад: снабженец следит за приходом товара.
      // Отдельного модуля закупок нет — они часть торговли.
      // Ревизия правил 07.10.2026: четыре стадии закупки — одно правило.
      'purchase_stalled',
      'supplier_request_silent',

      'stock_below_min',
      'batch_expiring',
      // Инвентаризация показывала расхождения и молча их применяла: пропало на
      // сорок тысяч — и никто не разбирался. Правилом это встаёт в общий ряд:
      // можно выключить, задать порог, увидеть прогноз перед включением.
      'stocktake_shortage',
      'stock_stale',
      // Просьба о скидке: механизм живёт в ценах и предложениях, то есть в
      // торговом контуре. Без склада правило не найдёт ничего — значит и
      // обещать его должен склад, а не менеджмент.
      'discount_waiting',
      // Торговый контур: без них менеджмент не видел половину дополнения.
      'seasonal_client_silent',
      'offer_expiring',
      // 08.10.2026: черновик и неотгруженный заказ — одно «Заказ стоит».
      'order_stalled',
      // 01.10.2026: принятое КП без заказа, черновики заказа и закупки.
      'offer_accepted_not_converted',
      // 08.10.2026: КП без ответа, оплата отгруженного заказа, оплата поставщику.
      'offer_no_answer',
      'order_payment_due',
      'purchase_payment_due',
      'delivery_not_closed',
      'contract_expiring',
    ],
  },
] as const satisfies readonly ModuleDef[]

/**
 * Ключи модулей, скрытых за глобальным флагом готовности.
 *
 * Выводится из самого реестра, а не перечисляется руками. Раньше список флагов
 * жил отдельным союзом в lib/flags/app-flags, и склад в него просто не попал:
 * три страницы обходили это через `isFlagEnabled('stock' as never)`, гася ровно
 * тот сигнал, ради которого союз и заводился.
 *
 * Только gated: у модуля без флага строки в app_flags нет, и проверка вернула
 * бы false, спрятав ядро линии бизнеса.
 */
export type GatedModuleKey = Extract<(typeof MODULES)[number], { gated: true }>['key']

export function getModule(key: string): ModuleDef | undefined {
  return MODULES.find(m => m.key === key)
}

/** Дополнения — то, что продаётся отдельной строкой поверх линии. */
export function addonModules(): readonly ModuleDef[] {
  return MODULES.filter(m => m.kind === 'addon')
}

/** Модули, скрытые за глобальным флагом готовности. */
export function gatedModules(): readonly ModuleDef[] {
  // `'gated' in m`, а не `m.gated`: у модулей без флага поля нет вовсе, и
  // литеральный тип реестра о нём не знает. Проверка наличия — единственная,
  // которая работает и на литералах, и в рантайме.
  return MODULES.filter(m => 'gated' in m && m.gated)
}

// 950: eventsOf() и списки `events` текстом убраны — их никто не читал.
// Что умеет модуль, покупателю показывают тарифы и онбординг из словаря
// (modules.<ключ>.title/summary/events), а движок правил берёт `eventKeys`.


/** Модули, которые продаются: у них есть цена и карточка в тарифах. */
export function soldModules(): readonly ModuleDef[] {
  return MODULES.filter(m => 'sold' in m && m.sold) as readonly ModuleDef[]
}

/**
 * Части продукта, включая его самого.
 *
 * Порядок важен: сам продукт первым, потом входящее. На экране это порядок
 * переключателей, и «Задачи» должны стоять раньше «Склада» — так же, как в
 * описании продукта.
 */
export function partsOf(key: ModuleKey): readonly ModuleDef[] {
  const self = MODULES.filter(m => m.key === key)
  const parts = MODULES.filter(m => 'partOf' in m && m.partOf === key)
  return [...self, ...parts] as readonly ModuleDef[]
}

/**
 * Какому модулю принадлежит маршрут. null — маршрут вне модулей (ядро).
 *
 * Одно место, где живёт это знание. Сайдбар спрашивает здесь, а не хранит
 * проверку у каждого пункта: пункты добавляют часто, и однажды забудут.
 *
 * Сравнение по сегментам, а не по началу строки: маршрут `stocktake` не должен
 * считаться частью модуля `stock` только потому, что начинается так же.
 */
export function moduleOfRoute(route: string): ModuleKey | null {
  const parts = route.replace(/^\/+/, '').split('/')
  for (const m of MODULES) {
    const routes = 'routes' in m ? (m.routes ?? []) : []
    for (const r of routes) {
      const rp = r.split('/')
      if (rp.every((seg, i) => parts[i] === seg)) return m.key as ModuleKey
    }
  }
  return null
}

// ============================================================================
// 1003 · «МОЙ ДЕНЬ» — СИСТЕМНОЕ ВНИМАНИЕ И БЕЗ «МЕНЕДЖМЕНТА».
// ----------------------------------------------------------------------------
// Решение владельца (1002-Б, 1003): «Мой день» показывает задачи от правил
// любой компании — это внимание системы, а не платный раздел. Без
// «Менеджмента» там только задачи от правил, без своих задач, откладывания и
// «Выполнено». Остальные страницы раздела задач (правила, «Контроль»,
// настройки, разговоры) — под «Менеджментом», как были.
// ============================================================================
export const SYSTEM_ATTENTION_ROUTE = 'tasks'

// Правила — раздел рабочего стола и открыты без «Менеджмента» целиком: свои
// правила, срок, вкл/выкл, уборка — любому сотруднику (решение 2026-10-02;
// прежде, в 1003, без модуля было только ядро). «Контроль» и настройки задач —
// по-прежнему модуль.
export const SYSTEM_ATTENTION_ROUTES: readonly string[] = [SYSTEM_ATTENTION_ROUTE, 'tasks/rules']

// Маршрут открыт и без модуля — ровно эти страницы, не их соседи.
export function routeOpenWithoutModule(route: string): boolean {
  return SYSTEM_ATTENTION_ROUTES.includes(route.replace(/^\/+|\/+$/g, ''))
}

// ============================================================================
// 946-B · ПРИМЕНИМОСТЬ РАЗДЕЛА — ОДНА ФУНКЦИЯ ДЛЯ МЕНЮ И НАСТРОЕК.
// ----------------------------------------------------------------------------
// Меню решало «показывать ли пункт» по реестру, а «Свои списки» показывали все
// разделы любой компании: импортёр без «Перевозок» настраивал статусы сделок,
// типы транспорта и колонки сделок. Теперь и меню, и настройки спрашивают
// одно: доступен ли маршрут компании — модуль маршрута включён (модули —
// resolveCompanyModules, 1002-Б · Б4), маршрут ядра или «Мой день».
// ============================================================================

// Доступен ли маршрут компании с такими модулями.
export function routeAllowed(route: string, modules: Record<string, boolean>): boolean {
  if (routeOpenWithoutModule(route)) return true
  const key = moduleOfRoute(route)
  return key ? modules[key] === true : true
}

// Раздел нужен компании, если ей доступен ЛЮБОЙ из его маршрутов; без маршрута —
// раздел ядра. Категории расходов нужны и сделкам (расходы рейса), и странице
// «Расходы» — одного маршрута им мало.
export function anyRouteAllowed(route: string | readonly string[] | undefined, modules: Record<string, boolean>): boolean {
  if (!route) return true
  return (typeof route === 'string' ? [route] : route).some(r => routeAllowed(r, modules))
}

// Раздел каталога колонок (lib/modules/catalog.ts) → маршрут, к которому он
// относится. null — раздел ядра (партнёры, финансы): доступен всем.
export const SECTION_ROUTES: Record<string, string | null> = {
  deals: 'deals',
  partners: null,
  tenders: 'tenders',
  purchases: 'purchases',
  offers: 'offers',
  orders: 'orders',
  stock: 'stock',
  finance: null,
  fleet_vehicles: 'fleet',
  fleet_drivers: 'fleet',
  // 946-B: лиды — ядро.
  leads: null,
}

// Доступен ли раздел каталога колонок компании. Неизвестный раздел — нет:
// новый раздел обязан объявить свой маршрут здесь (сторож — тест реестра).
export function sectionAllowed(sectionKey: string, modules: Record<string, boolean>): boolean {
  if (!(sectionKey in SECTION_ROUTES)) return false
  const route = SECTION_ROUTES[sectionKey]
  return route === null || routeAllowed(route, modules)
}
